import React, { useState } from 'react';
import { Button, Card, Form, Switch } from 'antd';

import { useTranslation } from 'react-i18next';
import ButtonComponent from '@/components/Button';
import InputComponent from '@/components/Input';
import SelectInput from '@/components/Select';
import TableComponent from '@/components/Table';
import { PAGE_SIZE, SMS_TEMPLATE_CONSTANT } from '@/constants';
import ModalComponent from '@/components/Modal';
import { API } from '@/constants/api';
import { SmsManagementService } from '@/services';
import { ReactComponent as SearchIcon } from '@/assets/images/icons/search.svg';
import { ReactComponent as PlusIcon } from '@/assets/images/icons/plus.svg';
import { ReactComponent as EditIcon } from '@/assets/images/icons/edit.svg';
import EllipsisTooltip from '@/components/EllipsisTooltip';
import SmsTemplateForm from './SmsTemplateForm';

import './sms-template.scss';

function SmsTemplateContainer() {
  const [filterForm] = Form.useForm();
  const [filterParams, setFilterParams] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [triggerRefreshTable, setTriggerRefreshTable] = useState(false);
  const [loadingSaveBtn, setLoadingSaveBtn] = useState(false);
  const [currentMode, setCurrentMode] = useState('create');
  const [currentRecord, setCurrentRecord] = useState({});
  const [modalTitle, setModalTitle] = useState();
  const { t } = useTranslation();

  const handleOnClickEditTemplate = (record) => {
    setCurrentRecord(record);
    setCurrentMode('edit');
    setModalTitle(t('sms_create_template.edit_template'));
    setModalOpen(true);
    setTriggerRefreshTable(false);
  };

  const handleUpdateStatus = async (record, checked) => {
    const resp = await SmsManagementService.updateSmsTemplate({
      id: record.id,
      isActive: checked,
    });

    if (resp.success) {
      setModalOpen(false);
      setTriggerRefreshTable(true);
    }

    setTimeout(() => {
      setTriggerRefreshTable(false);
    }, 200);
  };

  const columns = [
    {
      title: t('sms_create_template.fields.name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: {
        showTitle: false,
      },
      render: (_, { name }) => (
        <div className="fw-600">
          <EllipsisTooltip title={name}>{name}</EllipsisTooltip>
        </div>
      ),
    },
    {
      title: t('sms_page.quick_send.form.message_content.label'),
      dataIndex: 'content',
      key: 'content',
      ellipsis: {
        showTitle: false,
      },
      render: (_, { content }) => (
        <EllipsisTooltip title={content}>{content}</EllipsisTooltip>
      ),
    },
    {
      title: t('common.transactions_table.form_filter.status.label'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: '8%',
      render: (_, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleUpdateStatus(record, checked)}
        />
      ),
    },
    {
      title: '',
      dataIndex: '',
      key: 'actions',
      width: '6%',
      render: (_, record) => (
        <Button
          type="link"
          className="btn-edit"
          onClick={() => handleOnClickEditTemplate(record)}
        >
          <EditIcon />
        </Button>
      ),
    },
  ];

  const handleOnFormFilterFinish = (values) => {
    setTriggerRefreshTable(true);
    setFilterParams({
      ...filterParams,
      ...values,
      pagination: {
        current: 1,
        pageSize: PAGE_SIZE,
      },
    });

    setTimeout(() => {
      setTriggerRefreshTable(false);
    }, 50);
  };

  const handleResetFilterForm = () => {
    filterForm.resetFields();
    setFilterParams({});
  };

  const handleCreateSmsTemplate = async (values) => {
    setLoadingSaveBtn(true);
    const resp = await SmsManagementService.createSmsTemplate(values);

    if (resp.success) {
      setModalOpen(false);
      setTriggerRefreshTable(true);
    }
    setLoadingSaveBtn(false);
  };

  const handleUpdateSmsTemplate = async (values) => {
    setLoadingSaveBtn(true);
    const resp = await SmsManagementService.updateSmsTemplate({
      ...values,
      id: currentRecord.id,
    });

    if (resp.success) {
      setModalOpen(false);
      setTriggerRefreshTable(true);
    }
    setLoadingSaveBtn(false);
  };

  const handleOnSubmitTemplateForm = (values) => {
    if (currentMode === 'create') {
      handleCreateSmsTemplate(values);
    }

    if (currentMode === 'edit') {
      handleUpdateSmsTemplate(values);
    }
  };

  const handleOnClickAddNew = () => {
    setCurrentMode('create');
    setTriggerRefreshTable(false);
    setModalTitle(t('sms_create_template.add_template'));
    setCurrentRecord({});
    setModalOpen(true);
  };

  const smsTemplateStatusOptions = SMS_TEMPLATE_CONSTANT.STATUSES.map(
    (status) => ({
      label: t(
        `common.constants.sms_tempplate_constant.statuses.${status.label}`,
      ),
      value: status.value,
    }),
  );

  return (
    <div className="sms-template-container">
      <Card
        title={t('sms_template_list.sms_template')}
        bordered={false}
        extra={
          <ButtonComponent
            type="primary"
            icon={<PlusIcon />}
            className="btn-add-new"
            onClick={handleOnClickAddNew}
          >
            {t('sms_sender_list.add_new')}
          </ButtonComponent>
        }
      >
        <div className="form-filter">
          <Form
            name="filter"
            layout="vertical"
            onFinish={handleOnFormFilterFinish}
            autoComplete="off"
            className="form"
            form={filterForm}
          >
            <Form.Item
              label={t('sms_create_template.fields.name')}
              name="name"
              className="form-item"
            >
              <InputComponent
                placeholder={t('sms_create_template.fields.name')}
              />
            </Form.Item>
            <Form.Item
              label={t('common.transactions_table.form_filter.status.label')}
              name="isActive"
              className="form-item"
            >
              <SelectInput
                allowClear
                placeholder={t(
                  'common.transactions_table.form_filter.status.label',
                )}
                options={smsTemplateStatusOptions}
              />
            </Form.Item>
            <Form.Item>
              <div className="form-actions-item">
                <ButtonComponent
                  type="primary"
                  htmlType="submit"
                  icon={<SearchIcon />}
                >
                  {t('transactions_page_trans.search')}
                </ButtonComponent>
                <ButtonComponent type="default" onClick={handleResetFilterForm}>
                  {t('transactions_page_trans.reset')}
                </ButtonComponent>
              </div>
            </Form.Item>
          </Form>
        </div>

        <TableComponent
          triggerRefresh={triggerRefreshTable}
          columns={columns}
          filterParams={filterParams}
          action={API.SMS_MANAGERMENT.SMS_TEMPLATE.INDEX}
        />
      </Card>

      <ModalComponent
        title={modalTitle}
        centered
        open={modalOpen}
        footer={null}
        onCancel={() => setModalOpen(false)}
      >
        <SmsTemplateForm
          data={currentRecord}
          onSubmit={handleOnSubmitTemplateForm}
          loading={loadingSaveBtn}
        />
      </ModalComponent>
    </div>
  );
}

export default SmsTemplateContainer;
